import {
  useEffect,
  useMemo,
  useState
} from "react";

import {
  collection,
  doc,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from "../access/firestore";

import {
  Search,
  Plus,
  Pencil,
  Trash2,
  Pill,
  X,
  Save
} from "lucide-react";

import { db } from "../firebase";
import { useClinic } from "../context/ClinicContext";
import { medications as defaultMedications } from "../data/medications";
import { requestVexaConfirm } from "../notifications/dialogService";

const initialForm = {
  name: "",
  patientType: "Adulto",
  presentation: "Tabletas",
  doseValue: "",
  doseUnit: "mg",
  category: "Analgésico",
  interval: "Cada 8 horas",
  durationValue: "",
  durationUnit: "días",
  route: "VO",
  warnings: "",
  contraindications: "",
  allergyContraindications: ""
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "11px 12px",
  border: "1px solid #d1d5db",
  borderRadius: 11,
  fontSize: 14,
  background: "#fff"
};

const primaryButton = {
  border: "none",
  borderRadius: 11,
  padding: "11px 14px",
  background: "#2563eb",
  color: "#fff",
  fontWeight: 700,
  fontSize: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  cursor: "pointer"
};

const secondaryButton = {
  border: "1px solid #d1d5db",
  borderRadius: 11,
  padding: "10px 13px",
  background: "#fff",
  color: "#111827",
  fontWeight: 700,
  fontSize: 13,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 7,
  cursor: "pointer"
};

function MedicationCard({
  medicamento,
  onEdit,
  onDelete
}) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 17,
        padding: 15,
        boxShadow: "0 2px 8px rgba(15,23,42,0.04)"
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 10
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 16,
              color: "#0f172a"
            }}
          >
            {medicamento.name}
          </div>

          <div
            style={{
              marginTop: 4,
              fontSize: 12,
              color: "#64748b"
            }}
          >
            {medicamento.category || "Sin categoría"}
          </div>
        </div>

        <span
          style={{
            flexShrink: 0,
            padding: "4px 8px",
            borderRadius: 999,
            background:
              medicamento.origen === "custom"
                ? "#eff6ff"
                : "#f1f5f9",
            color:
              medicamento.origen === "custom"
                ? "#2563eb"
                : "#64748b",
            fontSize: 10,
            fontWeight: 700
          }}
        >
          {medicamento.origen === "custom"
            ? "Personalizado"
            : "VexaDent"}
        </span>
      </div>

      <div
        style={{
          marginTop: 13,
          display: "flex",
          flexDirection: "column",
          gap: 6,
          fontSize: 13,
          color: "#475569"
        }}
      >
        {medicamento.adultPresentation && (
          <div>
            <strong>Presentación:</strong>{" "}
            {medicamento.adultPresentation}
          </div>
        )}

        {medicamento.adultDose && (
          <div>
            <strong>Dosis:</strong>{" "}
            {medicamento.adultDose}
          </div>
        )}

        {medicamento.interval && (
          <div>
            <strong>Intervalo:</strong>{" "}
            {medicamento.interval}
          </div>
        )}

        {medicamento.duration && (
          <div>
            <strong>Duración:</strong>{" "}
            {medicamento.duration}
          </div>
        )}

        {medicamento.route && (
          <div>
            <strong>Vía:</strong>{" "}
            {medicamento.route}
          </div>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            medicamento.origen === "custom"
              ? "1fr 1fr"
              : "1fr",
          gap: 8,
          marginTop: 14
        }}
      >
        <button
          type="button"
          onClick={() => onEdit(medicamento)}
          style={{
            ...secondaryButton,
            width: "100%"
          }}
        >
          <Pencil size={15} />
          Editar
        </button>

        {medicamento.origen === "custom" && (
          <button
            type="button"
            onClick={() => onDelete(medicamento)}
            style={{
              ...secondaryButton,
              width: "100%",
              color: "#dc2626",
              borderColor: "#fecaca"
            }}
          >
            <Trash2 size={15} />
            Eliminar
          </button>
        )}
      </div>
    </div>
  );
}

export default function MobileMedicamentosSettings() {
  const { clinicId } = useClinic();

  const [medications, setMedications] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [editingMedication, setEditingMedication] =
    useState(null);

  const [showForm, setShowForm] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const [category, setCategory] =
    useState("Todas");

  const [form, setForm] =
    useState(initialForm);

  useEffect(() => {
    if (!clinicId) {
      setMedications(defaultMedications);
      setLoading(false);
      return undefined;
    }

    const medicamentosRef = collection(
      db,
      "clinics",
      clinicId,
      "medicamentosConfiguracion"
    );

    const unsubscribe = onSnapshot(
      medicamentosRef,
      snapshot => {
        const personalizados =
          snapshot.docs.map(item => ({
            id: item.id,
            ...item.data(),
            origen: "custom"
          }));

        const personalizadosPorBase =
          new Map(
            personalizados
              .filter(m => m.medicamentoBase)
              .map(m => [
                m.medicamentoBase,
                m
              ])
          );

        const medicamentosFinales =
          defaultMedications.map(
            medicamento => {
              const personalizado =
                personalizadosPorBase.get(
                  medicamento.name
                );

              return (
                personalizado ||
                medicamento
              );
            }
          );

        const personalizadosNuevos =
          personalizados.filter(
            medicamento =>
              !medicamento.medicamentoBase
          );

        setMedications([
          ...medicamentosFinales,
          ...personalizadosNuevos
        ]);

        setLoading(false);
      },
      error => {
        console.error(
          "Error cargando medicamentos:",
          error
        );

        setMedications(
          defaultMedications
        );

        setLoading(false);
      }
    );

    return unsubscribe;
  }, [clinicId]);

  const abrirNuevoMedicamento = () => {
    setEditingMedication(null);
    setForm({
      ...initialForm
    });
    setShowForm(true);
  };

  const guardarMedicamento = async () => {
    if (!clinicId) return;

    if (!form.name.trim()) {
      alert(
        "Escribe el nombre del medicamento."
      );
      return;
    }

    const datosBase =
      editingMedication &&
      editingMedication.origen !== "custom"
        ? {
            ...editingMedication
          }
        : {};

    delete datosBase.id;

    const datos = {
      ...datosBase,

      name: form.name.trim(),
      category: form.category,
      patientType: form.patientType,
      presentation: form.presentation,
      doseValue: form.doseValue,
      doseUnit: form.doseUnit,

      adultDose:
        form.doseValue &&
        form.doseUnit
          ? `${form.doseValue} ${form.doseUnit}`
          : "",

      interval: form.interval,

      durationValue:
        form.durationValue,

      durationUnit:
        form.durationUnit,

      duration:
        form.durationValue
          ? Number(form.durationValue)
          : "",

      route: form.route,

      adultPresentation:
        form.patientType === "Adulto" &&
        form.presentation
          ? `${form.presentation} ${
              form.doseValue || ""
            }${form.doseUnit || ""}`.trim()
          : "",

      pediatricPresentation:
        form.patientType !== "Adulto" &&
        form.presentation
          ? `${form.presentation} ${
              form.doseValue || ""
            }${form.doseUnit || ""}`.trim()
          : "",

      warnings: form.warnings
        .split(",")
        .map(item => item.trim())
        .filter(Boolean),

      contraindications:
        form.contraindications
          .split(",")
          .map(item => item.trim())
          .filter(Boolean),

      allergyContraindications:
        form.allergyContraindications
          .split(",")
          .map(item => item.trim())
          .filter(Boolean),

      origen: "custom",
      fechaActualizacion:
        serverTimestamp()
    };

    try {
      if (editingMedication) {
        if (
          editingMedication.origen ===
          "custom"
        ) {
          await updateDoc(
            doc(
              db,
              "clinics",
              clinicId,
              "medicamentosConfiguracion",
              editingMedication.id
            ),
            datos
          );
        } else {
          const medicamentoExistente =
            medications.find(
              medicamento =>
                medicamento.origen ===
                  "custom" &&
                medicamento.medicamentoBase ===
                  editingMedication.name
            );

          if (medicamentoExistente) {
            await updateDoc(
              doc(
                db,
                "clinics",
                clinicId,
                "medicamentosConfiguracion",
                medicamentoExistente.id
              ),
              datos
            );
          } else {
            await addDoc(
              collection(
                db,
                "clinics",
                clinicId,
                "medicamentosConfiguracion"
              ),
              {
                ...datos,
                medicamentoBase:
                  editingMedication.name,
                fechaCreacion:
                  serverTimestamp()
              }
            );
          }
        }
      } else {
        await addDoc(
          collection(
            db,
            "clinics",
            clinicId,
            "medicamentosConfiguracion"
          ),
          {
            ...datos,
            fechaCreacion:
              serverTimestamp()
          }
        );
      }

      setShowForm(false);
      setEditingMedication(null);
    } catch (error) {
      console.error(
        "Error guardando medicamento:",
        error
      );

      alert(
        "No fue posible guardar el medicamento."
      );
    }
  };

  const abrirEdicionMedicamento =
    medicamento => {
      setEditingMedication(
        medicamento
      );

      const presentacionOriginal =
        medicamento.adultPresentation ||
        medicamento.pediatricPresentation ||
        "";

      const texto =
        presentacionOriginal.toLowerCase();

      const presentacion =
        texto.includes("cápsula")
          ? "Cápsulas"
          : texto.includes("jarabe")
          ? "Jarabe"
          : texto.includes("suspensión")
          ? "Suspensión"
          : texto.includes("solución")
          ? "Solución"
          : texto.includes("ampolleta")
          ? "Ampolleta"
          : texto.includes("crema")
          ? "Crema"
          : texto.includes("gel")
          ? "Gel"
          : texto.includes("enjuague")
          ? "Enjuague"
          : texto.includes("gotas")
          ? "Gotas"
          : "Tabletas";

      const dosisMatch =
        presentacionOriginal.match(
          /(\d+(?:\.\d+)?)\s*(mg|ml|g|mcg|UI|%)/i
        );

      const intervalo =
        medicamento.interval === "4h"
          ? "Cada 4 horas"
          : medicamento.interval === "6h"
          ? "Cada 6 horas"
          : medicamento.interval === "8h"
          ? "Cada 8 horas"
          : medicamento.interval === "12h"
          ? "Cada 12 horas"
          : medicamento.interval === "24h"
          ? "Cada 24 horas"
          : medicamento.interval ===
            "Única"
          ? "Dosis única"
          : medicamento.interval ||
            "Cada 8 horas";

      setForm({
        name: medicamento.name || "",

        patientType:
          medicamento.patientType ||
          "Adulto",

        presentation: presentacion,

        doseValue:
          medicamento.doseValue ||
          dosisMatch?.[1] ||
          "",

        doseUnit:
          medicamento.doseUnit ||
          dosisMatch?.[2]?.toLowerCase() ||
          "mg",

        category:
          medicamento.category ||
          "Analgésico",

        interval: intervalo,

        durationValue:
          medicamento.durationValue ??
          medicamento.duration ??
          "",

        durationUnit:
          medicamento.durationUnit ||
          "días",

        route:
          medicamento.route ||
          medicamento.allowedRoutes?.[0] ||
          "VO",

        warnings:
          (
            medicamento.warnings ||
            []
          ).join(", "),

        contraindications:
          (
            medicamento.contraindications ||
            []
          ).join(", "),

        allergyContraindications:
          (
            medicamento.allergyContraindications ||
            []
          ).join(", ")
      });

      setShowForm(true);
    };

  const eliminarMedicamento =
    async medicamento => {
      if (
        medicamento.origen !==
        "custom"
      ) {
        return;
      }

      const confirmar =
        await requestVexaConfirm(
          `¿Eliminar "${medicamento.name}" del catálogo?`
        );

      if (!confirmar) return;

      try {
        await deleteDoc(
          doc(
            db,
            "clinics",
            clinicId,
            "medicamentosConfiguracion",
            medicamento.id
          )
        );
      } catch (error) {
        console.error(
          "Error eliminando medicamento:",
          error
        );

        alert(
          "No fue posible eliminar el medicamento."
        );
      }
    };

  const categorias = useMemo(
    () => [
      "Todas",
      ...new Set(
        medications
          .map(
            medication =>
              medication.category
          )
          .filter(Boolean)
      )
    ],
    [medications]
  );

  const medicamentosFiltrados =
    medications.filter(
      medication => {
        const coincideNombre =
          medication.name
            ?.toLowerCase()
            .includes(
              search.toLowerCase()
            );

        const coincideCategoria =
          category === "Todas" ||
          medication.category ===
            category;

        return (
          coincideNombre &&
          coincideCategoria
        );
      }
    );

  const medicamentosPredeterminados =
    medicamentosFiltrados.filter(
      medicamento =>
        medicamento.origen !== "custom"
    );

  const medicamentosPersonalizados =
    medicamentosFiltrados.filter(
      medicamento =>
        medicamento.origen === "custom"
    );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        paddingBottom: 20
      }}
    >
      <div>
        <h2
          style={{
            margin: 0,
            fontSize: 28,
            fontWeight: 700,
            color: "#0f172a"
          }}
        >
          Medicamentos
        </h2>

        <p
          style={{
            margin: "8px 0 0",
            color: "#64748b",
            fontSize: 14,
            lineHeight: 1.5
          }}
        >
          Administra el catálogo de medicamentos
          y su posología para tu consultorio.
        </p>
      </div>

      <button
        type="button"
        onClick={
          abrirNuevoMedicamento
        }
        style={{
          ...primaryButton,
          width: "100%",
          padding: "14px 16px",
          fontSize: 15
        }}
      >
        <Plus size={19} />
        Nuevo medicamento
      </button>

      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 18,
          padding: 14
        }}
      >
        <div
          style={{
            position: "relative"
          }}
        >
          <Search
            size={18}
            color="#94a3b8"
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform:
                "translateY(-50%)"
            }}
          />

          <input
            value={search}
            onChange={e =>
              setSearch(e.target.value)
            }
            placeholder="Buscar medicamento..."
            style={{
              ...inputStyle,
              paddingLeft: 39
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            overflowX: "auto",
            paddingTop: 11,
            paddingBottom: 2
          }}
        >
          {categorias.map(
            categoria => (
              <button
                key={categoria}
                type="button"
                onClick={() =>
                  setCategory(
                    categoria
                  )
                }
                style={{
                  flexShrink: 0,
                  border: "none",
                  borderRadius: 999,
                  padding:
                    "8px 12px",
                  background:
                    category ===
                    categoria
                      ? "#dbeafe"
                      : "#f1f5f9",
                  color:
                    category ===
                    categoria
                      ? "#1d4ed8"
                      : "#475569",
                  fontSize: 12,
                  fontWeight: 700
                }}
              >
                {categoria}
              </button>
            )
          )}
        </div>
      </div>

      {loading ? (
        <div
          style={{
            padding: 30,
            textAlign: "center",
            color: "#64748b"
          }}
        >
          Cargando medicamentos...
        </div>
      ) : medicamentosFiltrados.length ===
        0 ? (
        <div
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 18,
            padding: 30,
            textAlign: "center",
            color: "#64748b"
          }}
        >
          No se encontraron medicamentos.
        </div>
      ) : (
        <>
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
                marginBottom: 10
              }}
            >
              <Pill
                size={20}
                color="#2563eb"
              />

              <h3
                style={{
                  margin: 0,
                  fontSize: 18
                }}
              >
                Predeterminados
              </h3>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10
              }}
            >
              {medicamentosPredeterminados.map(
                medicamento => (
                  <MedicationCard
                    key={`default-${medicamento.id}`}
                    medicamento={
                      medicamento
                    }
                    onEdit={
                      abrirEdicionMedicamento
                    }
                    onDelete={
                      eliminarMedicamento
                    }
                  />
                )
              )}
            </div>
          </div>

          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
                marginBottom: 10
              }}
            >
              <Pill
                size={20}
                color="#2563eb"
              />

              <h3
                style={{
                  margin: 0,
                  fontSize: 18
                }}
              >
                Personalizados
              </h3>
            </div>

            {medicamentosPersonalizados.length ===
            0 ? (
              <div
                style={{
                  background: "#fff",
                  border:
                    "1px solid #e5e7eb",
                  borderRadius: 17,
                  padding: 18,
                  color: "#64748b",
                  fontSize: 13
                }}
              >
                Todavía no tienes medicamentos
                personalizados.
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10
                }}
              >
                {medicamentosPersonalizados.map(
                  medicamento => (
                    <MedicationCard
                      key={`custom-${medicamento.id}`}
                      medicamento={
                        medicamento
                      }
                      onEdit={
                        abrirEdicionMedicamento
                      }
                      onDelete={
                        eliminarMedicamento
                      }
                    />
                  )
                )}
              </div>
            )}
          </div>
        </>
      )}

      {showForm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background:
              "rgba(15,23,42,0.55)",
            zIndex: 99999,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center"
          }}
        >
          <div
            style={{
              width: "100%",
              maxHeight: "92vh",
              overflowY: "auto",
              background: "#fff",
              borderRadius:
                "24px 24px 0 0",
              padding: 18,
              boxSizing: "border-box"
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent:
                  "space-between",
                gap: 10,
                marginBottom: 18
              }}
            >
              <div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: 20
                  }}
                >
                  {editingMedication
                    ? "Editar medicamento"
                    : "Nuevo medicamento"}
                </h3>

                <div
                  style={{
                    marginTop: 4,
                    fontSize: 12,
                    color: "#64748b"
                  }}
                >
                  Configura presentación,
                  dosis y posología.
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingMedication(
                    null
                  );
                }}
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 11,
                  border:
                    "1px solid #e5e7eb",
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent:
                    "center"
                }}
              >
                <X size={19} />
              </button>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 13
              }}
            >
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#64748b"
                }}
              >
                Nombre
                <input
                  value={form.name}
                  onChange={e =>
                    setForm({
                      ...form,
                      name: e.target.value
                    })
                  }
                  placeholder="Nombre del medicamento"
                  style={{
                    ...inputStyle,
                    marginTop: 6
                  }}
                />
              </label>

              <label
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#64748b"
                }}
              >
                Tipo de paciente
                <select
                  value={
                    form.patientType
                  }
                  onChange={e =>
                    setForm({
                      ...form,
                      patientType:
                        e.target.value
                    })
                  }
                  style={{
                    ...inputStyle,
                    marginTop: 6
                  }}
                >
                  <option value="Adulto">
                    Adulto
                  </option>
                  <option value="Infantil">
                    Infantil
                  </option>
                  <option value="Pediátrico">
                    Pediátrico
                  </option>
                </select>
              </label>

              <label
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#64748b"
                }}
              >
                Presentación
                <select
                  value={
                    form.presentation
                  }
                  onChange={e =>
                    setForm({
                      ...form,
                      presentation:
                        e.target.value
                    })
                  }
                  style={{
                    ...inputStyle,
                    marginTop: 6
                  }}
                >
                  <option value="Tabletas">
                    Tabletas
                  </option>
                  <option value="Cápsulas">
                    Cápsulas
                  </option>
                  <option value="Jarabe">
                    Jarabe
                  </option>
                  <option value="Suspensión">
                    Suspensión
                  </option>
                  <option value="Solución">
                    Solución
                  </option>
                  <option value="Ampolleta">
                    Ampolleta
                  </option>
                  <option value="Crema">
                    Crema
                  </option>
                  <option value="Gel">
                    Gel
                  </option>
                  <option value="Enjuague">
                    Enjuague
                  </option>
                  <option value="Gotas">
                    Gotas
                  </option>
                  <option value="Otro">
                    Otro
                  </option>
                </select>
              </label>

              <div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#64748b",
                    marginBottom: 6
                  }}
                >
                  Dosis
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "1fr 100px",
                    gap: 8
                  }}
                >
                  <input
                    type="number"
                    value={
                      form.doseValue
                    }
                    onChange={e =>
                      setForm({
                        ...form,
                        doseValue:
                          e.target.value
                      })
                    }
                    placeholder="Dosis"
                    style={inputStyle}
                  />

                  <select
                    value={
                      form.doseUnit
                    }
                    onChange={e =>
                      setForm({
                        ...form,
                        doseUnit:
                          e.target.value
                      })
                    }
                    style={inputStyle}
                  >
                    <option value="mg">
                      mg
                    </option>
                    <option value="ml">
                      ml
                    </option>
                    <option value="g">
                      g
                    </option>
                    <option value="mcg">
                      mcg
                    </option>
                    <option value="UI">
                      UI
                    </option>
                    <option value="%">
                      %
                    </option>
                  </select>
                </div>
              </div>

              <label
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#64748b"
                }}
              >
                Categoría
                <select
                  value={
                    form.category
                  }
                  onChange={e =>
                    setForm({
                      ...form,
                      category:
                        e.target.value
                    })
                  }
                  style={{
                    ...inputStyle,
                    marginTop: 6
                  }}
                >
                  <option value="Analgésico">
                    Analgésico
                  </option>
                  <option value="AINE">
                    AINE
                  </option>
                  <option value="Antibiótico">
                    Antibiótico
                  </option>
                  <option value="Corticoide">
                    Corticoide
                  </option>
                  <option value="Antiséptico">
                    Antiséptico
                  </option>
                  <option value="Antiinflamatorio">
                    Antiinflamatorio
                  </option>
                  <option value="Anestésico">
                    Anestésico
                  </option>
                  <option value="Emergencia">
                    Emergencia
                  </option>
                  <option value="Profilaxis">
                    Profilaxis
                  </option>
                  <option value="Otro">
                    Otro
                  </option>
                </select>
              </label>

              <label
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#64748b"
                }}
              >
                Intervalo
                <select
                  value={
                    form.interval
                  }
                  onChange={e =>
                    setForm({
                      ...form,
                      interval:
                        e.target.value
                    })
                  }
                  style={{
                    ...inputStyle,
                    marginTop: 6
                  }}
                >
                  <option value="Cada 4 horas">
                    Cada 4 horas
                  </option>
                  <option value="Cada 6 horas">
                    Cada 6 horas
                  </option>
                  <option value="Cada 8 horas">
                    Cada 8 horas
                  </option>
                  <option value="Cada 12 horas">
                    Cada 12 horas
                  </option>
                  <option value="Cada 24 horas">
                    Cada 24 horas
                  </option>
                  <option value="Dosis única">
                    Dosis única
                  </option>
                  <option value="Según necesidad">
                    Según necesidad
                  </option>
                </select>
              </label>

              <div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#64748b",
                    marginBottom: 6
                  }}
                >
                  Duración
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "1fr 100px",
                    gap: 8
                  }}
                >
                  <input
                    type="number"
                    min="1"
                    value={
                      form.durationValue
                    }
                    onChange={e =>
                      setForm({
                        ...form,
                        durationValue:
                          e.target.value
                      })
                    }
                    placeholder="Duración"
                    style={inputStyle}
                  />

                  <select
                    value={
                      form.durationUnit
                    }
                    onChange={e =>
                      setForm({
                        ...form,
                        durationUnit:
                          e.target.value
                      })
                    }
                    style={inputStyle}
                  >
                    <option value="días">
                      días
                    </option>
                    <option value="semanas">
                      semanas
                    </option>
                    <option value="meses">
                      meses
                    </option>
                  </select>
                </div>
              </div>

              <label
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#64748b"
                }}
              >
                Vía de administración
                <select
                  value={form.route}
                  onChange={e =>
                    setForm({
                      ...form,
                      route: e.target.value
                    })
                  }
                  style={{
                    ...inputStyle,
                    marginTop: 6
                  }}
                >
                  <option value="VO">
                    Vía oral (VO)
                  </option>
                  <option value="SL">
                    Sublingual (SL)
                  </option>
                  <option value="IM">
                    Intramuscular (IM)
                  </option>
                  <option value="IV">
                    Intravenosa (IV)
                  </option>
                  <option value="SC">
                    Subcutánea (SC)
                  </option>
                  <option value="Tópica">
                    Tópica
                  </option>
                  <option value="Oftálmica">
                    Oftálmica
                  </option>
                  <option value="Ótica">
                    Ótica
                  </option>
                  <option value="Nasal">
                    Nasal
                  </option>
                  <option value="Bucal">
                    Bucal
                  </option>
                  <option value="Enjuague">
                    Enjuague
                  </option>
                  <option value="Otra">
                    Otra
                  </option>
                </select>
              </label>

              <label
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#64748b"
                }}
              >
                Advertencias
                <input
                  value={
                    form.warnings
                  }
                  onChange={e =>
                    setForm({
                      ...form,
                      warnings:
                        e.target.value
                    })
                  }
                  placeholder="Separadas por comas"
                  style={{
                    ...inputStyle,
                    marginTop: 6
                  }}
                />
              </label>

              <label
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#64748b"
                }}
              >
                Contraindicaciones
                <input
                  value={
                    form.contraindications
                  }
                  onChange={e =>
                    setForm({
                      ...form,
                      contraindications:
                        e.target.value
                    })
                  }
                  placeholder="Separadas por comas"
                  style={{
                    ...inputStyle,
                    marginTop: 6
                  }}
                />
              </label>

              <label
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#64748b"
                }}
              >
                Alergias
                <input
                  value={
                    form.allergyContraindications
                  }
                  onChange={e =>
                    setForm({
                      ...form,
                      allergyContraindications:
                        e.target.value
                    })
                  }
                  placeholder="Separadas por comas"
                  style={{
                    ...inputStyle,
                    marginTop: 6
                  }}
                />
              </label>
            </div>

            <div
              style={{
                display: "flex",
                gap: 9,
                marginTop: 20
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingMedication(
                    null
                  );
                }}
                style={{
                  ...secondaryButton,
                  flex: 1
                }}
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={
                  guardarMedicamento
                }
                style={{
                  ...primaryButton,
                  flex: 1
                }}
              >
                <Save size={17} />
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
